// Copie de AdminDashboard.js adaptée pour les archives (lecture seule)
// Les actions de correction (modification, pastilles cliquables) sont désactivées
// Les données sont lues depuis workSessionsArchive et breaksArchive
// Les exports CSV/PDF sont conservés

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase-config";
import { collection, getDocs, query } from "firebase/firestore";
import "./AdminDashboard.css";
import { jsPDF } from "jspdf";
const autoTable = require('jspdf-autotable').default;

const ADMIN_COLUMNS = [
  { key: 'name', label: 'Nom' },
  { key: 'date', label: 'Date' },
  { key: 'startTime', label: 'Heure Début' },
  { key: 'endTime', label: 'Heure Fin' },
  { key: 'workTime', label: 'Temps Travail' },
  { key: 'pauseTime', label: 'Temps Pause' },
  { key: 'sites', label: 'Sites autorisés' },
  { key: 'gpsStart', label: 'GPS Début' },
  { key: 'gpsEnd', label: 'GPS Fin' },
  { key: 'zoneStart', label: 'Zone Début' },
  { key: 'zoneEnd', label: 'Zone Fin' },
  { key: 'zonePause', label: 'Zone(s) Pause(s)' },
  { key: 'device', label: '📱 Appareil' },
  { key: 'info', label: 'Info' },
];

function AdminArchive() {
  const navigate = useNavigate();
  
  // State pour les onglets
  const [activeTab, setActiveTab] = useState('sessions'); // 'sessions' ou 'timesheets'
  
  // States pour les sessions
  const [workSessions, setWorkSessions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // States pour les feuilles de temps
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetsLoading, setTimesheetsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const sessionsSnapshot = await getDocs(query(collection(db, "workSessionsArchive")));
        const sessionDocs = sessionsSnapshot.docs;
        const breaksSnapshot = await getDocs(collection(db, "breaksArchive"));
        const allBreaks = breaksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const breaksBySession = allBreaks.reduce((acc, breakItem) => {
          if (!acc[breakItem.sessionId]) acc[breakItem.sessionId] = [];
          acc[breakItem.sessionId].push(breakItem);
          return acc;
        }, {});
        const sessionsData = sessionDocs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          breaks: breaksBySession[doc.id] || []
        }));
        setWorkSessions(sessionsData);
        // Charger aussi les employés et sites pour les labels
        const empSnap = await getDocs(collection(db, "employees"));
        setEmployees(empSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        const sitesSnap = await getDocs(collection(db, "sites"));
        setSites(sitesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (e) {
        setError("Erreur lors du chargement des archives");
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  // useEffect pour charger les feuilles de temps archivées
  useEffect(() => {
    if (activeTab === 'timesheets') {
      fetchTimesheets();
    }
  }, [activeTab]);

  const fetchTimesheets = async () => {
    setTimesheetsLoading(true);
    try {
      const timesheetsSnapshot = await getDocs(collection(db, "workSessionsWeekArchives"));
      const timesheetsData = timesheetsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Trier par date décroissante
      timesheetsData.sort((a, b) => {
        const dateA = new Date(a.weekStart || a.monthStart);
        const dateB = new Date(b.weekStart || b.monthStart);
        return dateB - dateA;
      });
      
      setTimesheets(timesheetsData);
    } catch (error) {
      console.error("Erreur chargement feuilles archivées:", error);
    }
    setTimesheetsLoading(false);
  };

  // Fonctions utilitaires (copiées/adaptées de AdminDashboard.js)
  const getTimeInSeconds = (session) => {
    if (session.startTime?.seconds) return session.startTime.seconds;
    if (session.startTime?.toDate) return Math.floor(session.startTime.toDate().getTime() / 1000);
    return 0;
  };

  const formatTime = (ms) => {
    if (!ms || isNaN(ms)) return '0h 0m';
    const totalMinutes = Math.round(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const getEmployeeDisplayName = (session) => {
    const emp = employees.find(e => e.email === session.employeeEmail);
    return emp ? `${emp.nom} ${emp.prenom}` : session.employeeName || session.employeeEmail || 'Inconnu';
  };

  const getZoneLabelSimple = (location, sites) => {
    if (!location) return "Non disponible";
    if (location.siteId) {
      const site = sites.find(s => s.id === location.siteId);
      return site ? site.nom : "HORS ZONE";
    }
    return "HORS ZONE";
  };

  const getPauseZonesForExport = (session, sites) => {
    if (!session.breaks || session.breaks.length === 0) return "Aucune pause";
    return session.breaks.map(breakItem => {
      // PRIORITÉ 1 : Utiliser le siteId stocké dans les données
      const siteId = breakItem.siteId;
      if (siteId === "HORS_ZONE") return "HORS ZONE";
      
      // PRIORITÉ 2 : Utiliser le siteId des locations si disponible
      const startSiteId = breakItem.startLocation?.siteId;
      const endSiteId = breakItem.endLocation?.siteId;
      
      if (startSiteId === "HORS_ZONE" || endSiteId === "HORS_ZONE") return "HORS ZONE";
      
      // PRIORITÉ 3 : Chercher les sites par ID
      const startSite = startSiteId ? sites.find(s => s.id === startSiteId) : null;
      const endSite = endSiteId ? sites.find(s => s.id === endSiteId) : null;
      
      const startSiteName = startSite ? startSite.nom : "Site inconnu";
      const endSiteName = endSite ? endSite.nom : "Site inconnu";
      
      // Si les sites sont différents, afficher les deux
      if (startSiteName !== endSiteName) {
        return `${startSiteName} → ${endSiteName}`;
      }
      
      return startSiteName;
    }).join(", ");
  };

  // Ajouter les mêmes fonctions que AdminDashboard pour la cohérence
  const getWorkDuration = (session) => {
    // PRIORITÉ 1 : Utiliser adjustedWorkTime s'il existe (calculé lors de la sauvegarde)
    if (session.adjustedWorkTime != null) {
      return formatTime(session.adjustedWorkTime);
    }
    
    // PRIORITÉ 2 : Si l'horaire a été modifié manuellement mais pas de adjustedWorkTime, recalculer
    if (session.adminModified && session.adjustedStartTime && session.adjustedEndTime) {
      const startTime = session.adjustedStartTime instanceof Date ? session.adjustedStartTime.getTime() : new Date(session.adjustedStartTime).getTime();
      const endTime = session.adjustedEndTime instanceof Date ? session.adjustedEndTime.getTime() : new Date(session.adjustedEndTime).getTime();
      const workTimeMs = Math.max(0, endTime - startTime - (session.totalPauseTime || 0));
      return formatTime(workTimeMs);
    }
    
    // PRIORITÉ 3 : Utiliser les horaires originaux
    if (session.startTime?.seconds && session.endTime?.seconds) {
      const workTimeMs = Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0));
      return formatTime(workTimeMs);
    }
    return '0h 0m';
  };

  const getPauseDuration = (session) => {
    return formatTime(session.totalPauseTime || 0);
  };

  // Ajouter la fonction pour les sites autorisés (comme dans AdminDashboard)
  const getEmployeeAuthorizedSites = (session) => {
    if (!session.employeeEmail) return "Non disponible";
    
    // Utiliser les sites autorisés stockés dans la session si disponibles
    if (session.authorizedSites && Array.isArray(session.authorizedSites)) {
      return session.authorizedSites.join(", ");
    }
    
    // Fallback vers l'ancien format
    if (session.siteDeTravail && Array.isArray(session.siteDeTravail)) {
      return session.siteDeTravail.join(", ");
    }
    
    return "Non disponible";
  };

  // Ajouter la fonction pour formater les coordonnées GPS (comme dans AdminDashboard)
  const formatGPSCoordinate = (location, coordinate) => {
    if (!location) return "Non disponible";
    const value = coordinate === "latitude" ? (location.latitude || location.lat) : (location.longitude || location.lng);
    return value ? value.toFixed(6) : "Non disponible";
  };

  // Fonctions simplifiées pour l'affichage des horaires (cohérentes avec les exports)
  const getStartTimeDisplay = (session) => {
    const startTime = getTimeInSeconds(session);
    return startTime ? new Date(startTime * 1000).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'Non disponible';
  };

  const getEndTimeDisplay = (session) => {
    const endTime = session.endTime?.seconds ? new Date(session.endTime.seconds * 1000) : null;
    return endTime ? endTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'En cours';
  };

  const getDateDisplay = (session) => {
    const startTime = getTimeInSeconds(session);
    return startTime ? new Date(startTime * 1000).toLocaleDateString('fr-FR') : 'Non disponible';
  };

  // Fonction unifiée pour l'affichage GPS
  const getGPSDisplay = (location) => {
    if (!location) return '';
    return `Lat: ${formatGPSCoordinate(location, 'latitude')}, Long: ${formatGPSCoordinate(location, 'longitude')}`;
  };

  // Filtres et pagination
  const months = Array.from(new Set(workSessions.map(session => {
    const startTime = getTimeInSeconds(session);
    if (!startTime) return null;
    const date = new Date(startTime * 1000);
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`;
  }))).filter(Boolean);

  const filteredSessions = workSessions
    .filter((session) => {
      if (selectedEmployee && session.employeeEmail !== selectedEmployee) return false;
      const startTimeSeconds = getTimeInSeconds(session);
      if (!startTimeSeconds) return false;
      if (selectedMonth === "all") return true;
      const startDate = new Date(startTimeSeconds * 1000);
      const sessionMonth = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, "0")}`;
      return sessionMonth === selectedMonth;
    })
    .sort((a, b) => getTimeInSeconds(b) - getTimeInSeconds(a));

  const pagedSessions = filteredSessions.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totalSessions = filteredSessions.length;

  // Export CSV (identique à AdminDashboard, mais lecture seule)
  const exportToCSV = async () => {
    const rows = [];
    const header = ADMIN_COLUMNS.map(col => col.label);
    rows.push(header);
    pagedSessions.forEach((session) => {
      const row = [];
      ADMIN_COLUMNS.forEach(col => {
        switch (col.key) {
          case 'name':
            row.push(getEmployeeDisplayName(session));
            break;
          case 'date':
            row.push(getDateDisplay(session));
            break;
          case 'startTime':
            row.push(getStartTimeDisplay(session));
            break;
          case 'endTime':
            row.push(getEndTimeDisplay(session));
            break;
          case 'workTime':
            row.push(getWorkDuration(session));
            break;
          case 'pauseTime':
            row.push(getPauseDuration(session));
            break;
          case 'sites':
            row.push(getEmployeeAuthorizedSites(session));
            break;
          case 'gpsStart':
            row.push(getGPSDisplay(session.startLocation));
            break;
          case 'gpsEnd':
            row.push(getGPSDisplay(session.endLocation));
            break;
          case 'zoneStart':
            row.push(getZoneLabelSimple(session.startLocation, sites));
            break;
          case 'zoneEnd':
            row.push(getZoneLabelSimple(session.endLocation, sites));
            break;
          case 'zonePause':
            row.push(getPauseZonesForExport(session, sites));
            break;
          case 'device':
            row.push(session.device || "Non spécifié");
            break;
          case 'info':
            row.push(session.isEscalationValidated || session.originalEscalationId ? 'Escalade' : session.adminModified ? 'Modifiée' : 'Auto');
            break;
          default:
            row.push("");
        }
      });
      rows.push(row);
    });
    const csvContent = "\uFEFF" + rows.map((r) => r.join(";")).join("\n");
    const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "archives_sessions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export PDF (identique à AdminDashboard, mais lecture seule)
  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const tableData = pagedSessions.map(session => ADMIN_COLUMNS.map(col => {
      switch (col.key) {
        case 'name': return getEmployeeDisplayName(session);
        case 'date': return getDateDisplay(session);
        case 'startTime': return getStartTimeDisplay(session);
        case 'endTime': return getEndTimeDisplay(session);
        case 'workTime': return getWorkDuration(session);
        case 'pauseTime': return getPauseDuration(session);
        case 'sites': return getEmployeeAuthorizedSites(session);
        case 'gpsStart': return getGPSDisplay(session.startLocation);
        case 'gpsEnd': return getGPSDisplay(session.endLocation);
        case 'zoneStart': return getZoneLabelSimple(session.startLocation, sites);
        case 'zoneEnd': return getZoneLabelSimple(session.endLocation, sites);
        case 'zonePause': return getPauseZonesForExport(session, sites);
        case 'device': return session.device || "Non spécifié";
        case 'info': return session.isEscalationValidated || session.originalEscalationId ? 'Escalade' : session.adminModified ? 'Modifiée' : 'Auto';
        default: return "";
      }
    }));
    autoTable(doc, {
      head: [ADMIN_COLUMNS.map(col => col.label)],
      body: tableData,
      styles: { fontSize: 8 },
    });
    doc.save("archives_sessions.pdf");
  };

  // Rendu
  return (
    <div className="admin-dashboard">
      <button className="admin-button" onClick={() => navigate("/admin")} style={{ marginBottom: 16 }}>Retour Admin</button>
      <h1>Archives</h1>
      
      {/* Onglets */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '20px',
        borderBottom: '2px solid #dee2e6'
      }}>
        <button
          onClick={() => setActiveTab('sessions')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'sessions' ? '3px solid #007bff' : 'none',
            background: activeTab === 'sessions' ? '#f8f9fa' : 'transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'sessions' ? '600' : '400',
            fontSize: '16px',
            color: activeTab === 'sessions' ? '#007bff' : '#6c757d',
            transition: 'all 0.2s'
          }}
        >
          📋 Sessions de travail
        </button>
        <button
          onClick={() => setActiveTab('timesheets')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'timesheets' ? '3px solid #007bff' : 'none',
            background: activeTab === 'timesheets' ? '#f8f9fa' : 'transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'timesheets' ? '600' : '400',
            fontSize: '16px',
            color: activeTab === 'timesheets' ? '#007bff' : '#6c757d',
            transition: 'all 0.2s'
          }}
        >
          📄 Feuilles de temps
        </button>
      </div>

      {/* Contenu de l'onglet Sessions */}
      {activeTab === 'sessions' && (
        <>
          <div className="filters">
        <label>
          Filtrer par salarié :
          <select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)}>
            <option value="">Tous</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.email}>
                {emp.nom} {emp.prenom}
              </option>
            ))}
          </select>
        </label>
        <label>
          Mois :
          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
            <option value="all">Tous</option>
            {months.map((month, idx) => (
              <option key={idx} value={month}>{month}</option>
            ))}
          </select>
        </label>
        <div className="export-buttons">
          <button onClick={exportToCSV}>📥 Exporter en CSV</button>
          <button onClick={exportToPDF}>📄 Exporter en PDF</button>
        </div>
      </div>
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <table className="session-table">
          <thead>
            <tr>
              {ADMIN_COLUMNS.map(col => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedSessions.map((session) => (
              <tr key={session.id}>
                <td style={{ textAlign: 'center' }}>{getEmployeeDisplayName(session)}</td>
                <td style={{ textAlign: 'center' }}>{getDateDisplay(session)}</td>
                <td style={{ textAlign: 'center' }}>{getStartTimeDisplay(session)}</td>
                <td style={{ textAlign: 'center' }}>{getEndTimeDisplay(session)}</td>
                <td style={{ fontWeight: 'bold', textAlign: 'center' }}>{getWorkDuration(session)}</td>
                <td style={{ textAlign: 'center' }}>{getPauseDuration(session)}</td>
                <td style={{ textAlign: 'center' }}>{getEmployeeAuthorizedSites(session)}</td>
                <td style={{ textAlign: 'center' }}>{getGPSDisplay(session.startLocation)}</td>
                <td style={{ textAlign: 'center' }}>{getGPSDisplay(session.endLocation)}</td>
                <td style={{ textAlign: 'center' }}>{getZoneLabelSimple(session.startLocation, sites)}</td>
                <td style={{ textAlign: 'center' }}>{getZoneLabelSimple(session.endLocation, sites)}</td>
                <td style={{ textAlign: 'center' }}>{getPauseZonesForExport(session, sites)}</td>
                <td style={{ textAlign: 'center' }}>{session.device || "Non précisé"}</td>
                <td style={{ textAlign: 'center' }}>
                  {session.isEscalationValidated || session.originalEscalationId ? (
                    <span style={{ background: '#ff9800', width: 16, height: 16, borderRadius: '50%', display: 'inline-block' }} title="Session issue d'une escalade"></span>
                  ) : session.adminModified ? (
                    <span style={{ background: '#ffe066', width: 16, height: 16, borderRadius: '50%', display: 'inline-block' }} title="Session automatique modifiée manuellement"></span>
                  ) : (
                    <span style={{ background: '#2196f3', width: 16, height: 16, borderRadius: '50%', display: 'inline-block' }} title="Session automatique"></span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 16, gap: 16}}>
        <span>Éléments par page :</span>
        <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }} style={{padding: '4px 8px', borderRadius: 6}}>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
        <span>{totalSessions === 0 ? '0' : `${page * rowsPerPage + 1} – ${Math.min((page + 1) * rowsPerPage, totalSessions)} sur ${totalSessions}`}</span>
        <button onClick={() => setPage(0)} disabled={page === 0} style={{background: 'none', border: 'none', fontSize: 18, cursor: page === 0 ? 'default' : 'pointer', color: '#444'}}>&#124;&lt;</button>
        <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{background: 'none', border: 'none', fontSize: 18, cursor: page === 0 ? 'default' : 'pointer', color: '#444'}}>&lt;</button>
        <button onClick={() => setPage(p => Math.min(Math.ceil(totalSessions / rowsPerPage) - 1, p + 1))} disabled={page >= Math.ceil(totalSessions / rowsPerPage) - 1} style={{background: 'none', border: 'none', fontSize: 18, cursor: page >= Math.ceil(totalSessions / rowsPerPage) - 1 ? 'default' : 'pointer', color: '#444'}}>&gt;</button>
        <button onClick={() => setPage(Math.max(0, Math.ceil(totalSessions / rowsPerPage) - 1))} disabled={page >= Math.ceil(totalSessions / rowsPerPage) - 1} style={{background: 'none', border: 'none', fontSize: 18, cursor: page >= Math.ceil(totalSessions / rowsPerPage) - 1 ? 'default' : 'pointer', color: '#444'}}>&gt;&#124;</button>
      </div>
        </>
      )}

      {/* Contenu de l'onglet Feuilles de temps */}
      {activeTab === 'timesheets' && (
        <div>
          {timesheetsLoading ? (
            <p>Chargement des feuilles de temps archivées...</p>
          ) : timesheets.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px',
              background: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <p style={{ fontSize: '18px', color: '#666' }}>
                🔭 Aucune feuille de temps archivée
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                background: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    <th style={{ padding: 12, textAlign: 'left' }}>Employé</th>
                    <th style={{ padding: 12, textAlign: 'left' }}>Période</th>
                    <th style={{ padding: 12, textAlign: 'center' }}>Sessions</th>
                    <th style={{ padding: 12, textAlign: 'center' }}>Temps travail</th>
                    <th style={{ padding: 12, textAlign: 'center' }}>Absences</th>
                    <th style={{ padding: 12, textAlign: 'center' }}>Statut</th>
                    <th style={{ padding: 12, textAlign: 'center' }}>Archivé le</th>
                  </tr>
                </thead>
                <tbody>
                  {timesheets.map((timesheet, idx) => (
                    <tr key={timesheet.id} style={{ 
                      borderBottom: '1px solid #dee2e6',
                      background: idx % 2 === 0 ? 'white' : '#f8f9fa'
                    }}>
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600 }}>{timesheet.employeeName}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>{timesheet.employeeEmail}</div>
                      </td>
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600 }}>
                          {timesheet.monthName ? `${timesheet.monthName} ${timesheet.year}` : `Semaine du ${formatDateFR(timesheet.weekStart)}`}
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {formatDateFR(timesheet.weekStart || timesheet.monthStart)} - {formatDateFR(timesheet.weekEnd || timesheet.monthEnd)}
                        </div>
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        {timesheet.sessionCount || 0}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center', fontWeight: 600 }}>
                        {formatTime(timesheet.totalWorkTime)}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        {timesheet.absenceCount || 0}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <span style={{
                          background: '#e2e3e5',
                          color: '#383d41',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {timesheet.status || 'Archivé'}
                        </span>
                      </td>
                      <td style={{ padding: 12, textAlign: 'center', fontSize: 12, color: '#666' }}>
                        {timesheet.archivedAt ? new Date(timesheet.archivedAt.seconds * 1000).toLocaleDateString('fr-FR') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Fonction utilitaire pour formater les dates
function formatDateFR(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR');
}

// Fonction utilitaire pour formater le temps
function formatTime(ms) {
  if (!ms || isNaN(ms)) return '0h00';
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.round((ms % 3600000) / 60000);
  return `${hours}h${minutes.toString().padStart(2, '0')}`;
}

export default AdminArchive;